import { useEffect, useRef } from "react";

interface UseWorkspaceOnboardingAutoTriggerOptions {
  needsOnboarding: boolean;
  onboardingAgentExists: boolean;
  hasMessages: boolean;
  hasConversation: boolean;
  hasAnyConversation?: boolean;
  conversationInventoryLoaded?: boolean;
  setupComplete?: boolean;
  isLoading: boolean;
  isInProgress: () => boolean;
  onStarted: () => void;
  sendOnboarding: () => Promise<unknown>;
  onError: (error: unknown) => void;
  onFailed: () => void;
}

/** Send the hidden setup prompt unless settings are ready or its durable marker exists. */
export function useWorkspaceOnboardingAutoTrigger({
  needsOnboarding,
  onboardingAgentExists,
  hasMessages,
  hasConversation,
  hasAnyConversation = false,
  conversationInventoryLoaded = true,
  setupComplete = false,
  isLoading,
  isInProgress,
  onStarted,
  sendOnboarding,
  onError,
  onFailed,
}: UseWorkspaceOnboardingAutoTriggerOptions): void {
  const triggeredRef = useRef(false);
  const optionsRef = useRef<UseWorkspaceOnboardingAutoTriggerOptions | null>(
    null,
  );
  optionsRef.current = {
    needsOnboarding,
    onboardingAgentExists,
    hasMessages,
    hasConversation,
    hasAnyConversation,
    conversationInventoryLoaded,
    setupComplete,
    isLoading,
    isInProgress,
    onStarted,
    sendOnboarding,
    onError,
    onFailed,
  };

  useEffect(() => {
    const current = optionsRef.current;
    if (
      !current ||
      !current.needsOnboarding ||
      current.setupComplete ||
      current.onboardingAgentExists ||
      triggeredRef.current ||
      current.isInProgress() ||
      current.hasMessages ||
      current.hasConversation ||
      current.hasAnyConversation ||
      !current.conversationInventoryLoaded ||
      current.isLoading
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      const latest = optionsRef.current;
      if (
        !latest ||
        !latest.needsOnboarding ||
        latest.setupComplete ||
        latest.onboardingAgentExists ||
        latest.hasAnyConversation ||
        !latest.conversationInventoryLoaded ||
        latest.hasMessages ||
        latest.hasConversation ||
        latest.isLoading ||
        latest.isInProgress()
      )
        return;
      triggeredRef.current = true;
      latest.onStarted();
      void latest.sendOnboarding().catch((error) => {
        latest.onError(error);
        latest.onFailed();
      });
    }, 500);

    return () => window.clearTimeout(timer);
  }, [
    needsOnboarding,
    onboardingAgentExists,
    hasMessages,
    hasConversation,
    hasAnyConversation,
    conversationInventoryLoaded,
    setupComplete,
    isLoading,
  ]);
}
